import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Plus, Trash2, Loader2, Check } from "lucide-react";
import { ADULT, PEDIATRIC, chartOf } from "../lib/toothNumbers";

const DentalChart = ({ patientId, readOnly = false }) => {
  const [chartType, setChartType] = useState("adult"); // adult | pediatric
  const [teethMap, setTeethMap] = useState({});
  const [conditions, setConditions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [newCond, setNewCond] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rec, conds] = await Promise.all([
        api.get(`/patients/${patientId}/dental-record`),
        api.get("/conditions"),
      ]);
      const map = {};
      (rec.data.teeth || []).forEach((t) => {
        map[t.tooth] = { conditions: t.conditions || [], notes: t.notes || "" };
      });
      setTeethMap(map);
      setConditions(conds.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (patientId) load(); /* eslint-disable-next-line */ }, [patientId]);

  const toothColor = (tn) => {
    const conds = teethMap[tn]?.conditions || [];
    if (conds.length === 0) return "#ffffff";
    if (conds.includes("Missing") || conds.includes("Extracted")) return "#94a3b8";
    if (conds.includes("Cavity")) return "#fecaca";
    if (conds.includes("Root Canal")) return "#fed7aa";
    if (conds.includes("Filled")) return "#bfdbfe";
    if (conds.includes("Crown") || conds.includes("Bridge") || conds.includes("Implant")) return "#fde68a";
    if (conds.includes("Healthy")) return "#bbf7d0";
    return "#e0e7ff";
  };

  const toggleCondition = (name) => {
    if (readOnly || selected == null) return;
    setTeethMap((prev) => {
      const cur = prev[selected] || { conditions: [], notes: "" };
      const has = cur.conditions.includes(name);
      const nextConds = has ? cur.conditions.filter((c) => c !== name) : [...cur.conditions, name];
      return { ...prev, [selected]: { ...cur, conditions: nextConds } };
    });
  };

  const setNotes = (val) => {
    if (readOnly || selected == null) return;
    setTeethMap((prev) => ({
      ...prev,
      [selected]: { ...(prev[selected] || { conditions: [] }), notes: val },
    }));
  };

  const addCustom = async () => {
    const name = newCond.trim();
    if (!name) return;
    try {
      const res = await api.post("/conditions", { name });
      setConditions((c) => [...c, res.data]);
      setNewCond("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add condition");
    }
  };

  const deleteCustom = async (id) => {
    if (!window.confirm("Delete this custom condition?")) return;
    try {
      await api.delete(`/conditions/${id}`);
      setConditions((c) => c.filter((x) => x.id !== id));
    } catch {
      alert("Failed to delete");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const teeth = Object.entries(teethMap)
        .filter(([, v]) => (v.conditions?.length || 0) > 0 || (v.notes || "").trim().length > 0)
        .map(([tooth, v]) => ({ tooth, conditions: v.conditions || [], notes: v.notes || "" }));
      await api.put(`/patients/${patientId}/dental-record`, { teeth });
      setSaving(false);
    } catch {
      setSaving(false);
      alert("Save failed");
    }
  };

  // If a tooth is selected but user switches chart type, keep selection only if it belongs
  useEffect(() => {
    if (selected && chartOf(selected) !== chartType) setSelected(null);
  }, [chartType, selected]);

  const currentTooth = selected != null ? teethMap[selected] : null;
  const T = chartType === "pediatric" ? PEDIATRIC : ADULT;

  const renderRow = (list) => (
    <div className="flex gap-1.5 justify-center">
      {list.map((tn) => {
        const isSel = selected === String(tn);
        const has = (teethMap[tn]?.conditions?.length || 0) > 0;
        return (
          <button
            key={tn}
            onClick={() => !readOnly && setSelected(String(tn))}
            disabled={readOnly && !has}
            className={`w-9 h-11 rounded-md border-2 flex flex-col items-center justify-center transition-all text-[10px] font-semibold ${
              isSel ? "border-[#0F2A5F] ring-2 ring-[#0F2A5F]/30" : "border-slate-300 hover:border-slate-400"
            } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
            style={{ background: toothColor(tn) }}
            title={`Tooth ${tn}${has ? ` — ${(teethMap[tn].conditions || []).join(", ")}` : ""}`}
          >
            <span className="text-slate-700">{tn}</span>
            {has && <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6] mt-0.5" />}
          </button>
        );
      })}
    </div>
  );

  if (loading) return <div className="p-8 text-center text-slate-400 text-sm">Loading chart...</div>;

  // Count of teeth with data per chart
  const adultMarked = Object.keys(teethMap).filter((k) => chartOf(k) === "adult").length;
  const pediMarked = Object.keys(teethMap).filter((k) => chartOf(k) === "pediatric").length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-slate-800">Examination Chart (FDI)</h3>
            <p className="text-xs text-slate-500 mt-0.5">Click a tooth to mark conditions</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
              <button
                onClick={() => setChartType("adult")}
                className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                  chartType === "adult" ? "bg-white text-[#0F2A5F] shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Adult {adultMarked > 0 && <span className="ml-1 text-[9px] bg-[#0F2A5F] text-white px-1 rounded">{adultMarked}</span>}
              </button>
              <button
                onClick={() => setChartType("pediatric")}
                className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${
                  chartType === "pediatric" ? "bg-white text-[#0F2A5F] shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                Pediatric {pediMarked > 0 && <span className="ml-1 text-[9px] bg-[#0F2A5F] text-white px-1 rounded">{pediMarked}</span>}
              </button>
            </div>
            {!readOnly && (
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white text-sm px-4 py-2 rounded-md disabled:opacity-70"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Save Chart
              </button>
            )}
          </div>
        </div>

        <div className="space-y-3 py-4">
          <div className="flex items-center gap-2">
            <div className="text-[10px] w-6 text-right text-slate-400">UR</div>
            {renderRow(T.UPPER_RIGHT)}
            <div className="w-px h-10 bg-slate-200 mx-1" />
            {renderRow(T.UPPER_LEFT)}
            <div className="text-[10px] w-6 text-left text-slate-400">UL</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-[10px] w-6 text-right text-slate-400">LR</div>
            {renderRow(T.LOWER_RIGHT)}
            <div className="w-px h-10 bg-slate-200 mx-1" />
            {renderRow(T.LOWER_LEFT)}
            <div className="text-[10px] w-6 text-left text-slate-400">LL</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          {[
            ["Healthy", "#bbf7d0"],
            ["Cavity", "#fecaca"],
            ["Filled", "#bfdbfe"],
            ["Root Canal", "#fed7aa"],
            ["Crown/Bridge/Implant", "#fde68a"],
            ["Missing/Extracted", "#94a3b8"],
          ].map(([lbl, c]) => (
            <div key={lbl} className="flex items-center gap-1.5 text-slate-600">
              <span className="w-3 h-3 rounded border border-slate-300" style={{ background: c }} />
              {lbl}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        {selected == null ? (
          <div className="text-center py-8 text-slate-500 text-sm">Select a tooth to view / edit</div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs text-slate-500">Tooth</div>
                <div className="text-2xl font-bold text-slate-800">{selected}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>

            <div className="mb-3">
              <div className="text-xs font-medium text-slate-600 mb-2">Conditions</div>
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {conditions.map((c) => {
                  const checked = currentTooth?.conditions?.includes(c.name) || false;
                  return (
                    <label key={c.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCondition(c.name)}
                          disabled={readOnly}
                          className="w-4 h-4 accent-[#0F2A5F]"
                        />
                        <span className="text-sm text-slate-700">{c.name}</span>
                        {c.is_custom && <span className="text-[10px] text-slate-400">(custom)</span>}
                      </div>
                      {c.is_custom && !readOnly && (
                        <button onClick={(e) => { e.preventDefault(); deleteCustom(c.id); }} className="text-slate-300 hover:text-red-600">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {!readOnly && (
              <div className="mb-3">
                <div className="text-xs font-medium text-slate-600 mb-1">Add custom condition</div>
                <div className="flex gap-2">
                  <input
                    value={newCond}
                    onChange={(e) => setNewCond(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                    placeholder="e.g. Watch"
                    className="flex-1 px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
                  />
                  <button onClick={addCustom} className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-slate-700">
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-medium text-slate-600 mb-1">Notes</div>
              <textarea
                rows={3}
                readOnly={readOnly}
                value={currentTooth?.notes || ""}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DentalChart;
